import type { IPipelineService, PipelineWriteInput } from './IPipelineService.interface'
import type { PipelineConfig, PipelineSummary } from '../../types/pipeline.types'
import { slug } from '../../lib/deployBranch'

// ── Seeded demo configurations ──────────────────────────────────────────────
// Two pipelines so the picker has something to switch between.

const MAIN_RELEASE: PipelineConfig = {
  id:              'main-release',
  name:            'Main release',
  projectId:       'default',
  repo:            'acme/sfdx',
  gitConnectionId: 'conn-git',
  stages: [
    {
      id:           'stage-dev',
      name:         'Dev',
      connectionId: 'conn-dev',
      branch:       'develop',
      envType:      'developer',
      alias:        'dev-01',
      sfdxAlias:    'dev-01',
      instanceUrl:  'https://dev-ed.develop.my.salesforce.com',
    },
    {
      id:           'stage-sit',
      name:         'SIT',
      connectionId: 'conn-sit',
      branch:       'sit',
      envType:      'sandbox',
      alias:        'sit-sbx',
      sfdxAlias:    'sit-sbx',
      instanceUrl:  'https://sit-sbx.sandbox.my.salesforce.com',
    },
    {
      id:           'stage-uat',
      name:         'UAT',
      connectionId: 'conn-uat',
      branch:       'uat',
      envType:      'sandbox',
      alias:        'uat-sbx',
      sfdxAlias:    'uat-sbx',
      instanceUrl:  'https://uat-sbx.sandbox.my.salesforce.com',
    },
    {
      id:           'stage-prod',
      name:         'Prod',
      connectionId: 'conn-prod',
      branch:       'main',
      envType:      'production',
      alias:        'prod-full',
      sfdxAlias:    'prod-full',
      instanceUrl:  'https://stratuscarta-c.my.salesforce.com',
    },
  ],
  transitions: [
    { id: 't-dev-sit',  from: 'stage-dev',  to: 'stage-sit',  mode: 'manual', gate: false, approval: 'none' },
    { id: 't-sit-uat',  from: 'stage-sit',  to: 'stage-uat',  mode: 'manual', gate: true,  approval: 'none' },
    { id: 't-uat-prod', from: 'stage-uat',  to: 'stage-prod', mode: 'manual', gate: true,  approval: 'required' },
  ],
  author:    'stride-demo',
  createdAt: new Date(Date.now() - 7 * 86_400_000).toISOString(),
  updatedAt: new Date(Date.now() - 86_400_000).toISOString(),
}

const HOTFIX: PipelineConfig = {
  id:              'hotfix',
  name:            'Hotfix',
  projectId:       'default',
  repo:            'acme/sfdx',
  gitConnectionId: 'conn-git',
  stages: [
    {
      id:           'stage-dev',
      name:         'Dev',
      connectionId: 'conn-dev',
      branch:       'develop',
      envType:      'developer',
      alias:        'dev-01',
      sfdxAlias:    'dev-01',
      instanceUrl:  'https://dev-ed.develop.my.salesforce.com',
    },
    {
      id:           'stage-prod',
      name:         'Prod',
      connectionId: 'conn-prod',
      branch:       'main',
      envType:      'production',
      alias:        'prod-full',
      sfdxAlias:    'prod-full',
      instanceUrl:  'https://stratuscarta-c.my.salesforce.com',
    },
  ],
  transitions: [
    { id: 't-dev-prod', from: 'stage-dev', to: 'stage-prod', mode: 'manual', gate: true, approval: 'required' },
  ],
  author:    'stride-demo',
  createdAt: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  updatedAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
}

function clone(c: PipelineConfig): PipelineConfig {
  return {
    ...c,
    stages:      c.stages.map((s) => ({ ...s })),
    transitions: c.transitions.map((t) => ({ ...t })),
  }
}

// ── MockPipelineService ───────────────────────────────────────────────────────

export class MockPipelineService implements IPipelineService {
  private readonly configs = new Map<string, PipelineConfig>([
    [MAIN_RELEASE.id, clone(MAIN_RELEASE)],
    [HOTFIX.id,       clone(HOTFIX)],
  ])

  listPipelines(): Promise<PipelineSummary[]> {
    const summaries: PipelineSummary[] = [...this.configs.values()].map((c) => ({
      id:              c.id,
      name:            c.name,
      projectId:       c.projectId,
      stageCount:      c.stages.length,
      transitionCount: c.transitions.length,
      updatedAt:       c.updatedAt,
    }))
    summaries.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    return Promise.resolve(summaries)
  }

  getPipeline(id: string): Promise<PipelineConfig> {
    const config = this.configs.get(id)
    if (!config) return Promise.reject(new Error(`Pipeline '${id}' not found`))
    return Promise.resolve(clone(config))
  }

  createPipeline(input: PipelineWriteInput): Promise<PipelineConfig> {
    const id = slug(input.name)
    if (this.configs.has(id)) {
      return Promise.reject(new Error(`a pipeline named '${input.name}' already exists`))
    }
    const now = new Date().toISOString()
    const config: PipelineConfig = {
      ...input,
      id,
      projectId: 'default',
      createdAt: now,
      updatedAt: now,
    }
    this.configs.set(id, clone(config))
    return Promise.resolve(clone(config))
  }

  updatePipeline(id: string, input: PipelineWriteInput): Promise<PipelineConfig> {
    const existing = this.configs.get(id)
    if (!existing) return Promise.reject(new Error(`Pipeline '${id}' not found`))
    const updated: PipelineConfig = {
      ...existing,
      ...input,
      id,                       // rename changes name only; id is stable
      projectId: existing.projectId,
      updatedAt: new Date().toISOString(),
    }
    this.configs.set(id, clone(updated))
    return Promise.resolve(clone(updated))
  }

  deletePipeline(id: string): Promise<void> {
    if (!this.configs.has(id)) return Promise.reject(new Error(`Pipeline '${id}' not found`))
    this.configs.delete(id)
    return Promise.resolve()
  }
}
